import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  inject,
  signal,
  computed,
  OnInit,
  OnDestroy,
  AfterViewChecked, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { LayoutService } from '@/layout/service/layout.service';
import { DeviceContextService } from '../device-context.service';
import { Subscription, filter } from 'rxjs';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { deviceDisplayName } from '@/cloud/services/device-display';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';

@Component({
  selector: 'app-cloud-remote-console',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    PasswordModule,
    ButtonModule,
    SelectButtonModule,
    DialogModule,
    TooltipModule,
    MessageModule,
    SectionCardComponent
  ],
  templateUrl: './cloud-remote-console.component.html',
  styleUrl: './cloud-remote-console.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CloudRemoteConsoleComponent implements OnInit, OnDestroy, AfterViewChecked {
  /** Template helper — see deviceDisplayName for the precedence. */
  displayName = deviceDisplayName;

  private api = inject(CloudApiService);
  private ctx = inject(DeviceContextService);
  private layoutService = inject(LayoutService);
  private eventSub: Subscription | null = null;
  private terminal: Terminal | null = null;

  /** Re-theme the live terminal when the colour scheme flips (xterm reads options at render time). */
  private readonly terminalThemeEffect = effect(() => {
    const dark = this.layoutService.isDarkTheme();
    if (this.terminal) this.terminal.options.theme = this.buildTerminalTheme(dark);
  });
  private fitAddon: FitAddon | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private pendingTerminalInit = false;
  private pendingInitMessage: string | null = null;

  // Device context
  device = this.ctx.device;
  deviceId = computed(() => this.device()?.id ?? '');

  // Tunnel state
  tunnelConnected = signal(false);
  tunnelChecking = signal(true);

  // Connection form
  protocol = signal<'ssh' | 'telnet' | 'ctp' | 'securectp'>('ssh');
  protocolOptions = [
    { label: 'SSH', value: 'ssh' },
    { label: 'Telnet', value: 'telnet' },
    { label: 'CTP', value: 'ctp' },
    { label: 'Secure CTP', value: 'securectp' }
  ];
  hostname = signal('');
  port = signal(22);
  username = signal('');
  password = signal('');

  // Session state
  sessionId = signal<string | null>(null);
  sessionStatus = signal<string>('Disconnected');
  errorMessage = signal<string | null>(null);
  isConnecting = signal(false);
  pendingRequestId = signal<string | null>(null);

  // Host key prompt
  showHostKeyPrompt = signal(false);
  pendingHostKeyFingerprint = signal<string | null>(null);
  pendingHostKeyType = signal<string | null>(null);
  pendingHostKeySessionId = signal<string | null>(null);

  // Computed
  isConnected = computed(() => this.sessionStatus() === 'Connected');
  protocolDisplayName = computed(() => {
    const labels: Record<string, string> = { ssh: 'SSH', telnet: 'Telnet', ctp: 'CTP', securectp: 'Secure CTP' };
    return labels[this.protocol()] ?? this.protocol().toUpperCase();
  });
  showConnectionForm = computed(() =>
    !this.sessionId() || this.sessionStatus() === 'Disconnected' || this.sessionStatus() === 'Error'
  );

  @ViewChild('terminalContainer') terminalContainer?: ElementRef<HTMLDivElement>;

  ngOnInit(): void {
    this.checkTunnelStatus();
    this.subscribeToEvents();
    this.checkExistingSessions();
  }

  ngAfterViewChecked(): void {
    if (this.pendingTerminalInit && !this.terminal && this.terminalContainer?.nativeElement) {
      this.tryInitTerminal();
    }
  }

  ngOnDestroy(): void {
    const id = this.deviceId();
    if (id) {
      this.api.unsubscribeFromRemoteConsole(id);
    }
    this.eventSub?.unsubscribe();
    this.destroyTerminal();
  }

  onProtocolChange(): void {
    const proto = this.protocol();
    switch (proto) {
      case 'ssh': this.port.set(22); break;
      case 'telnet': this.port.set(23); break;
      case 'ctp': this.port.set(41795); break;
      case 'securectp': this.port.set(41797); break;
    }
  }

  async connect(): Promise<void> {
    const host = this.hostname().trim();
    const devId = this.deviceId();
    if (!host || !devId) return;

    this.isConnecting.set(true);
    this.errorMessage.set(null);

    try {
      const result = await this.api.remoteConsoleConnect(
        devId,
        host,
        this.port(),
        this.protocol(),
        this.username(),
        this.password()
      );

      this.pendingRequestId.set(result.requestId);
      this.sessionStatus.set('Connecting');
      // The actual sessionId will come via the connectResult event
    } catch (e: unknown) {
      const msg = typeof e === 'string' ? e : (e instanceof Error ? e.message : 'Connection failed');
      this.errorMessage.set(msg);
      this.isConnecting.set(false);
    }
  }

  async disconnect(): Promise<void> {
    const sid = this.sessionId();
    const devId = this.deviceId();
    if (!sid || !devId) return;

    try {
      await this.api.remoteConsoleDisconnect(devId, sid);
    } catch {
      // ignore errors during disconnect
    }

    this.destroyTerminal();
    this.sessionId.set(null);
    this.sessionStatus.set('Disconnected');
    this.errorMessage.set(null);
    this.showHostKeyPrompt.set(false);
    this.pendingRequestId.set(null);
  }

  async acceptHostKey(accept: boolean): Promise<void> {
    const sid = this.pendingHostKeySessionId();
    const devId = this.deviceId();
    if (!sid || !devId) return;

    this.showHostKeyPrompt.set(false);
    this.pendingHostKeyFingerprint.set(null);
    this.pendingHostKeyType.set(null);
    this.pendingHostKeySessionId.set(null);

    try {
      await this.api.remoteConsoleAcceptHostKey(devId, sid, accept);
    } catch {
      // ignore errors
    }

    if (!accept) {
      this.destroyTerminal();
      this.sessionId.set(null);
      this.sessionStatus.set('Disconnected');
      this.errorMessage.set('Host key rejected');
    }
  }

  focusTerminal(): void {
    this.terminal?.focus();
  }

  // ----- Tunnel status -----

  async checkTunnelStatus(): Promise<void> {
    const devId = this.deviceId();
    if (!devId) {
      this.tunnelChecking.set(false);
      return;
    }

    try {
      const status = await this.api.getDeviceTunnelStatus(devId);
      this.tunnelConnected.set(status.tunnelConnected);
    } catch {
      this.tunnelConnected.set(false);
    } finally {
      this.tunnelChecking.set(false);
    }
  }

  // ----- Terminal lifecycle -----

  private scheduleTerminalInit(initialMessage?: string): void {
    this.pendingTerminalInit = true;
    if (initialMessage) this.pendingInitMessage = initialMessage;
    setTimeout(() => this.tryInitTerminal());
  }

  private tryInitTerminal(): void {
    if (!this.pendingTerminalInit || this.terminal) return;
    if (!this.terminalContainer?.nativeElement) return;

    this.pendingTerminalInit = false;
    this.initTerminal();
    this.flushPendingMessage();
  }

  private flushPendingMessage(): void {
    if (this.pendingInitMessage && this.terminal) {
      this.terminal.write(this.pendingInitMessage);
      this.pendingInitMessage = null;
    }
  }

  private resolveThemeColor(prop: string, fallback: string): string {
    try {
      const val = getComputedStyle(document.documentElement).getPropertyValue(prop).trim();
      if (val && !val.startsWith('var(')) return val;
    } catch { /* ignore */ }
    return fallback;
  }

  /**
   * ANSI palette for the terminal. Background/foreground/primary follow the app tokens; the 16 ANSI
   * colours come from a dark (VS Code Dark+) or light (VS Code Light+) set so output stays readable
   * in both schemes.
   */
  private buildTerminalTheme(dark: boolean): Record<string, string> {
    const bg = this.resolveThemeColor('--surface-ground', dark ? '#1e1e1e' : '#f3f4f6');
    const fg = this.resolveThemeColor('--text-color', dark ? '#d4d4d4' : '#1f2937');
    const primary = this.resolveThemeColor('--primary-color', '#7ab800');
    const ansi = dark
      ? {
          selectionBackground: 'rgba(255, 255, 255, 0.18)',
          black: '#000000', red: '#f44747', green: '#6a9955', yellow: '#d7ba7d',
          magenta: '#c586c0', cyan: '#4ec9b0', white: '#d4d4d4',
          brightBlack: '#808080', brightRed: '#f44747', brightGreen: '#6a9955', brightYellow: '#d7ba7d',
          brightMagenta: '#c586c0', brightCyan: '#4ec9b0', brightWhite: '#ffffff'
        }
      : {
          selectionBackground: 'rgba(0, 0, 0, 0.14)',
          black: '#000000', red: '#cd3131', green: '#00845d', yellow: '#946e00',
          magenta: '#af00db', cyan: '#0598bc', white: '#555555',
          brightBlack: '#6b7280', brightRed: '#cd3131', brightGreen: '#14ce14', brightYellow: '#b5ba00',
          brightMagenta: '#bc05bc', brightCyan: '#0598bc', brightWhite: '#a5a5a5'
        };
    return { background: bg, foreground: fg, cursor: fg, blue: primary, brightBlue: primary, ...ansi };
  }

  private initTerminal(): void {
    if (this.terminal) return;

    const container = this.terminalContainer?.nativeElement;
    if (!container) return;

    this.terminal = new Terminal({
      cursorBlink: true,
      cursorStyle: 'block',
      fontSize: 14,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Consolas', monospace",
      theme: this.buildTerminalTheme(this.layoutService.isDarkTheme()),
      scrollback: 10000
    });

    this.fitAddon = new FitAddon();
    this.terminal.loadAddon(this.fitAddon);

    this.terminal.open(container);
    this.fitAddon.fit();

    // Send user keystrokes to the remote session via cloud tunnel
    this.terminal.onData((data: string) => {
      const sid = this.sessionId();
      const devId = this.deviceId();
      if (sid && devId && this.isConnected()) {
        this.api.remoteConsoleWrite(devId, sid, data).catch(() => { /* ignore */ });
      }
    });

    this.resizeObserver = new ResizeObserver(() => {
      queueMicrotask(() => this.fitAddon?.fit());
    });
    this.resizeObserver.observe(container);

    this.terminal.focus();
  }

  private destroyTerminal(): void {
    this.pendingTerminalInit = false;
    this.pendingInitMessage = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.terminal?.dispose();
    this.terminal = null;
    this.fitAddon = null;
  }

  // ----- WebSocket Events -----

  private subscribeToEvents(): void {
    const devId = this.deviceId();
    if (!devId) return;

    // Subscribe to remote console WebSocket topics
    this.api.subscribeToRemoteConsole(devId);

    this.eventSub = this.api.remoteConsoleEvents$.pipe(
      filter(e => e !== null && e.deviceId === devId)
    ).subscribe(event => {
      if (!event) return;

      switch (event.type) {
        case 'output':
          if (event.data?.sessionId === this.sessionId()) {
            this.terminal?.write(event.data.data || '');
          }
          break;

        case 'statusChanged':
          if (event.data?.sessionId === this.sessionId() || event.data?.sessionId === this.pendingHostKeySessionId()) {
            this.sessionStatus.set(event.data.status);

            if (event.data.status === 'AwaitingHostKeyAcceptance') {
              this.showHostKeyPrompt.set(true);
              this.pendingHostKeySessionId.set(event.data.sessionId);
            } else if (event.data.status === 'Connected') {
              this.scheduleTerminalInit();
            } else if (event.data.status === 'Error') {
              this.errorMessage.set(event.data.errorMessage || 'Connection error');
            } else if (event.data.status === 'Disconnected') {
              this.errorMessage.set(event.data.errorMessage || 'Disconnected');
              this.destroyTerminal();
              this.sessionId.set(null);
            }
          }
          break;

        case 'connectResult':
          if (event.data?.requestId === this.pendingRequestId()) {
            this.isConnecting.set(false);
            this.pendingRequestId.set(null);

            if (event.data.sessionId) {
              this.sessionId.set(event.data.sessionId);
              this.sessionStatus.set(event.data.status || 'Connecting');

              if (event.data.status === 'Error') {
                this.errorMessage.set(event.data.errorMessage || 'Connection failed');
              } else {
                this.scheduleTerminalInit();
              }
            } else {
              this.errorMessage.set(event.data.errorMessage || 'Connection failed');
              this.sessionStatus.set('Disconnected');
            }
          }
          break;

        case 'hostKeyInfo':
          if (event.data?.sessionId === this.pendingHostKeySessionId() || event.data?.sessionId === this.sessionId()) {
            if (event.data.pendingHostKeyFingerprint) {
              this.pendingHostKeyFingerprint.set(event.data.pendingHostKeyFingerprint);
              this.pendingHostKeyType.set(event.data.pendingHostKeyType);
            }
          }
          break;

        case 'sessions':
          this.handleSessionsList(event.data?.sessions);
          break;
      }
    });
  }

  // ----- Session Resume -----

  private async checkExistingSessions(): Promise<void> {
    const devId = this.deviceId();
    if (!devId) return;

    try {
      // This sends a list request to the device; the response arrives via the 'sessions' event
      await this.api.remoteConsoleListSessions(devId);
    } catch {
      // ignore - device may not be connected yet
    }
  }

  private handleSessionsList(sessions: any[] | undefined): void {
    if (!sessions || !Array.isArray(sessions) || this.sessionId()) return;

    const active = sessions.find((s: any) => s.status === 'Connected');
    if (active) {
      this.sessionId.set(active.sessionId);
      this.sessionStatus.set(active.status);
      if (active.host) this.hostname.set(active.host);
      if (active.port) this.port.set(active.port);
      if (active.protocol) this.protocol.set(active.protocol as 'ssh' | 'telnet' | 'ctp' | 'securectp');
      this.scheduleTerminalInit('\x1b[2m[Resumed existing session]\x1b[0m\r\n');
    }
  }
}
